// Network utilities with retry logic

interface FetchOptions extends RequestInit {
  baseUrl?: string;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  retryStatusCodes?: number[];
}

interface FetchResponse<T> {
  data: T | null;
  error: Error | null;
  status: number;
  headers: Headers;
}

/**
 * Enhanced fetch function with retry logic, timeout, and error handling
 */
export async function fetchWithRetry<T>(
  url: string,
  options: FetchOptions = {}
): Promise<FetchResponse<T>> {
  const {
    baseUrl = '',
    timeout = 10000,
    retries = 3,
    retryDelay = 1000,
    retryStatusCodes = [408, 429, 500, 502, 503, 504],
    ...fetchOptions
  } = options;

  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  
  // Add default headers if not provided
  if (!fetchOptions.headers) {
    fetchOptions.headers = {
      'Content-Type': 'application/json',
    };
  }

  let attempt = 0;
  let lastError: Error | null = null;
  let lastStatus = 0;
  let lastHeaders = new Headers();

  while (attempt <= retries) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      // Add signal to fetch options
      const fetchOptionsWithSignal = {
        ...fetchOptions,
        signal: controller.signal,
      };

      // Execute fetch
      const response = await fetch(fullUrl, fetchOptionsWithSignal);
      clearTimeout(timeoutId);
      
      lastStatus = response.status;
      lastHeaders = response.headers;

      // Check if response should trigger a retry
      if (retryStatusCodes.includes(response.status) && attempt < retries) {
        attempt++;
        await delay(retryDelay * Math.pow(2, attempt - 1)); // Exponential backoff
        continue;
      }

      // Handle successful response
      if (response.ok) {
        // Check content type to determine how to parse the response
        const contentType = response.headers.get('content-type');
        let data: T | null = null;
        
        if (contentType && contentType.includes('application/json')) {
          data = await response.json() as T;
        } else if (contentType && contentType.includes('text/')) {
          data = await response.text() as unknown as T;
        } else {
          // For other content types (like blobs), just return the response
          data = response as unknown as T;
        }

        return { data, error: null, status: response.status, headers: response.headers };
      } else {
        // Handle error response
        let errorMessage: string;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `HTTP error ${response.status}`;
        } catch (e) {
          errorMessage = `HTTP error ${response.status}`;
        }
        
        const error = new Error(errorMessage);
        return { data: null, error, status: response.status, headers: response.headers };
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Handle abort error (timeout)
      if (lastError.name === 'AbortError') {
        lastError = new Error(`Request timeout after ${timeout}ms`);
      }
      
      // Check if we should retry
      if (attempt < retries) {
        attempt++;
        await delay(retryDelay * Math.pow(2, attempt - 1)); // Exponential backoff
        continue;
      }
      
      return { data: null, error: lastError, status: lastStatus, headers: lastHeaders };
    }
  }

  // This should never be reached, but TypeScript requires a return statement
  return { 
    data: null, 
    error: lastError || new Error('Unknown error'), 
    status: lastStatus, 
    headers: lastHeaders 
  };
}

/**
 * Helper function to create a delay
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * GET request with retry logic
 */
export async function get<T>(url: string, options: FetchOptions = {}): Promise<FetchResponse<T>> {
  return fetchWithRetry<T>(url, { ...options, method: 'GET' });
}

/**
 * POST request with retry logic
 */
export async function post<T>(url: string, data: any, options: FetchOptions = {}): Promise<FetchResponse<T>> {
  return fetchWithRetry<T>(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * PUT request with retry logic
 */
export async function put<T>(url: string, data: any, options: FetchOptions = {}): Promise<FetchResponse<T>> {
  return fetchWithRetry<T>(url, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * DELETE request with retry logic
 */
export async function del<T>(url: string, options: FetchOptions = {}): Promise<FetchResponse<T>> {
  return fetchWithRetry<T>(url, { ...options, method: 'DELETE' });
}

/**
 * Check if the device is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Add online/offline event listeners
 */
export function addNetworkStatusListeners(
  onOnline: () => void,
  onOffline: () => void
): () => void {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}